/**
 * 钉钉平台适配器
 * 封装钉钉多维表格 API 调用
 */

/* global Dingdocs */

import type {
  IPlatformAdapter,
  FieldInfo,
  RecordData,
  SendMessageParams,
} from "../types";
import { sendMessageToReceivers } from "../services/api";

// 声明全局 Dingdocs 类型
declare const Dingdocs: any;

export class DingTalkAdapter implements IPlatformAdapter {
  private dingdocsInstance: any = null;
  private isInitialized: boolean = false;
  private initPromise: Promise<boolean> | null = null;

  /**
   * 初始化Dingdocs（只执行一次）
   */
  private async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return this.dingdocsInstance !== null;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      const startTime = Date.now();
      const timeout = 3000;

      while (Date.now() - startTime < timeout) {
        if (typeof window !== "undefined") {
          const globalDingdocs = (window as any).Dingdocs;
          if (globalDingdocs && globalDingdocs.base) {
            this.dingdocsInstance = globalDingdocs;
            this.isInitialized = true;
            return true;
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.isInitialized = true;
      console.warn("⚠️ Dingdocs初始化超时，将使用模拟数据");
      return false;
    })();

    return this.initPromise;
  }

  /**
   * 检查Dingdocs是否可用
   */
  private isDingdocsAvailable(): boolean {
    return this.dingdocsInstance !== null;
  }

  /**
   * 获取Dingdocs对象
   */
  private getDingdocs(): any {
    return this.dingdocsInstance;
  }

  /**
   * 获取当前活动的表格（返回一个代理对象，实际操作通过 Script 层执行）
   */
  async getCurrentTable(): Promise<any> {
    try {
      await this.initialize();

      if (!this.isDingdocsAvailable()) {
        console.warn("⚠️ Dingdocs不可用，无法获取当前表格");
        throw new Error("Dingdocs 环境不可用");
      }

      // 返回一个代理对象，getFields 和 getRecords 将通过 Script 层执行
      return {
        id: "current-table",
        name: "当前表格",
        // getFields 方法会被 DingTalkAdapter.getFields 调用
        getFields: () => {
          return "CALL_SCRIPT_LAYER_getCurrentTableFields";
        },
        // getRecords 方法会被 DingTalkAdapter.getRecords 调用
        getRecords: () => {
          return "CALL_SCRIPT_LAYER_getCurrentTableRecords";
        },
      };
    } catch (error) {
      console.error("❌ 获取当前表格失败:", error);
      throw error;
    }
  }

  /**
   * 根据名称获取表格
   */
  async getTableByName(name: string): Promise<any> {
    try {
      await this.initialize();

      if (!this.isDingdocsAvailable()) {
        console.warn(`获取表格"${name}"失败，使用模拟数据`);
        // 返回模拟数据
        return {
          id: "mock-table",
          name: name,
          getFields: async () => [],
          getRecords: async () => [],
        };
      }

      const Dingdocs = this.getDingdocs();
      if (name === "消息模板表") {
        return {
          id: "message-template-table",
          name,
          getFields: async () => [], // 可补充字段定义
          getRecords: async () =>
            await Dingdocs.script.run("getMessageTemplates"),
        };
      }
      if (name === "业务标识配置表") {
        const configs = await Dingdocs.script.run("getBusinessKeyConfigs");
        return {
          id: "business-key-table",
          name,
          getFields: async () => [
            {
              id: "field1",
              name: "业务标识名称",
              type: "text",
              isPrimary: true,
            },
            {
              id: "field2",
              name: "biz_key前缀",
              type: "text",
              isPrimary: false,
            },
            { id: "field3", name: "描述", type: "text", isPrimary: false },
          ],
          getRecords: async () => configs,
        };
      }
      // 其它表...
      return {
        id: name,
        name,
        getFields: async () => [],
        getRecords: async () => [],
      };
    } catch (error) {
      console.error("获取表格失败:", error);
      throw error;
    }
  }

  /**
   * 获取表格字段（通过 Script 层执行）
   */
  async getFields(table: any): Promise<FieldInfo[]> {
    try {
      // 检查是否需要通过 Script 层执行
      const fieldsResult = await table.getFields();

      if (fieldsResult === "CALL_SCRIPT_LAYER_getCurrentTableFields") {
        const Dingdocs = this.getDingdocs();
        const fields = await Dingdocs.script.run("getCurrentTableFields");
        return fields;
      }

      // 否则按原逻辑处理

      if (!Array.isArray(fieldsResult)) {
        console.warn("⚠️ 返回的字段不是数组:", fieldsResult);
        return [];
      }

      if (fieldsResult.length === 0) {
        console.warn("⚠️ 字段列表为空");
        return [];
      }

      // 转换为标准格式
      const formatted = fieldsResult.map((field: any) => {
        const fieldInfo = {
          id: field.getId ? field.getId() : field.id || "",
          name: field.getName ? field.getName() : field.name || "",
          type: field.getType ? field.getType() : field.type || "text",
          isPrimary: field.isPrimary
            ? field.isPrimary()
            : field.isPrimary || false,
        };
        return fieldInfo;
      });

      return formatted;
    } catch (error) {
      console.error("❌ [DingTalkAdapter.getFields] 获取字段列表失败:", error);
      // 返回空数组而不是抛出错误
      return [];
    }
  }

  /**
   * 获取表格记录（通过 Script 层执行）
   */
  async getRecords(
    table: any,
    options?: { pageSize?: number }
  ): Promise<RecordData[]> {
    try {
      // 检查是否需要通过 Script 层执行
      const recordsResult = await table.getRecords(options);

      if (recordsResult === "CALL_SCRIPT_LAYER_getCurrentTableRecords") {
        const Dingdocs = this.getDingdocs();
        const records = await Dingdocs.script.run("getCurrentTableRecords");
        return records;
      }

      // 否则按原逻辑处理

      if (!Array.isArray(recordsResult)) {
        console.warn("⚠️ 返回的记录不是数组:", recordsResult);
        return [];
      }

      // 转换为标准格式
      const formatted = recordsResult.map((record: any) => ({
        id: record.getId ? record.getId() : record.id || "",
        fields: record.fields || {},
      }));

      return formatted;
    } catch (error) {
      console.error("❌ [DingTalkAdapter.getRecords] 获取记录列表失败:", error);
      // 返回空数组而不是抛出错误
      return [];
    }
  }

  /**
   * 发送消息（调用后端 API）
   */
  async sendMessage(params: SendMessageParams): Promise<number> {
    try {
      const messageId = await sendMessageToReceivers(params);
      return messageId;
    } catch (error) {
      throw error;
    }
  }
}
